import { ObjectId } from 'bson';
import capitalize from 'lodash.capitalize';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import type { LabelProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { ContentType as LabelContentType } from 'recharts/types/component/Label';
import type {
  ContentType as TooltipContentType,
  TooltipProps,
} from 'recharts/types/component/Tooltip';

import type {
  AggregatedStat,
  IGoal,
  Segment,
  Segments as SegmentsCollection,
  TAttributionModel,
} from '@feathr/blackbox';
import { colors } from '@feathr/components';
import { cssVar } from '@feathr/hooks';
import type { ReportStore } from '@feathr/report_components/state';

import * as styles from './ConversionBreakdownChart.css';

const { shade } = colors;

export interface IChartData {
  id: string;
  name?: string;
  roi: number;
  num: number;
  segmentId: string;
  goalId?: string;
  category?: string;
}

interface ISegmentColor {
  segment: string;
  color: string;
}

export interface IBreakdownData {
  segmentPieDataFormatted: IChartData[];
  goalPieDataFormatted: IChartData[];
  categoryPieDataFormatted: IChartData[];
  showSegsChart: boolean;
  showGoalsChart: boolean;
  showCatsChart: boolean;
  showConversionBreakdown: boolean;
  showROIBreakdown: boolean;
  showBreakdowns: boolean;
  breakdownSize: 'sm' | 'lg';
  segmentColors: ISegmentColor[];
  goalColors: ISegmentColor[];
  catColors: ISegmentColor[];
  conversionsLabelContent: LabelContentType;
  roiLabelContent: LabelContentType;
}

const CELL_COLORS = [
  cssVar('--color-primary-3'),
  cssVar('--color-brand-teal'),
  cssVar('--color-brand-green'),
  cssVar('--color-brand-yellow'),
  cssVar('--color-brand-orange'),
  cssVar('--color-brand-purple'),
];

export function getGoalSegments(goals: IGoal[], Segments: SegmentsCollection): Segment[] {
  return goals.filter((g) => !!g.segment && !g.is_archived).map((g) => Segments.get(g.segment!));
}

export function getInnerRadius(
  index: number,
  size: 'sm' | 'lg',
  showCats: boolean,
  showGoals: boolean,
  showSegs: boolean,
): number {
  const charts = [showCats, showGoals, showSegs];
  const numChartsShowing = charts.filter((flag) => flag).length;
  const sizeFactor = { sm: 1.2, lg: 1.4 }[size];
  let radius = 30;
  if (numChartsShowing === 3) {
    if (index === 1) {
      radius = 60;
    } else if (index === 2) {
      radius = 90;
    }
  } else if (numChartsShowing === 2) {
    if ((index === 1 && charts[0]) || index === 2) {
      radius = 75;
    }
  }
  return radius * sizeFactor;
}

export function getOuterRadius(
  index: number,
  size: 'sm' | 'lg',
  showCats: boolean,
  showGoals: boolean,
  showSegs: boolean,
): number {
  const charts = [showCats, showGoals, showSegs];
  const numChartsShowing = charts.filter((flag) => flag).length;
  const sizeFactor = { sm: 1.2, lg: 1.4 }[size];
  let radius = 120;
  if (numChartsShowing === 3) {
    if (index === 0) {
      radius = 60;
    } else if (index === 1) {
      radius = 90;
    }
  } else if (numChartsShowing === 2) {
    if (index === 0 || (index === 1 && charts[2])) {
      radius = 75;
    }
  }
  return radius * sizeFactor;
}

export function getCenterX(index: number, size: 'sm' | 'lg'): number {
  if (size === 'sm') {
    return index ? 560 : 210;
  }
  return 350;
}

export const LeftLabelContent: LabelContentType = ({ value }: LabelProps) => {
  return React.createElement('text', { textAnchor: 'middle', x: 210, y: 375 }, value);
};

export const CenterLabelContent: LabelContentType = ({ value }: LabelProps) => {
  return React.createElement('text', { textAnchor: 'middle', x: 350, y: 375 }, value);
};

export const RightLabelContent: LabelContentType = ({ value }: LabelProps) => {
  return React.createElement('text', { textAnchor: 'middle', x: 560, y: 375 }, value);
};

export function breakdownTooltipRenderer({
  Campaigns,
  Flights,
  Goals,
  Segments,
}: Readonly<ReportStore>): TooltipContentType<ValueType, NameType> {
  return function ({ active, payload: payloadProp }: TooltipProps<ValueType, NameType>) {
    if (!active || !payloadProp?.length) {
      return null;
    }

    const payload = payloadProp[0].payload;
    const ttsegment = payload?.segmentId ? Segments.get(payload.segmentId) : undefined;
    const ttgoal = payload?.goalId ? Goals.get(payload.goalId) : undefined;
    const ttcampaign =
      ttgoal && ttgoal.get('kind') !== 'flight' ? Campaigns.get(ttgoal.get('parent')) : undefined;
    const ttflight =
      ttgoal?.get('kind') === 'flight' ? Flights.get(ttgoal?.get('parent') ?? '') : undefined;
    const category = payload?.category;
    const ttroi = payload?.roi;
    const ttnum = payload?.num;
    return React.createElement(
      'div',
      { className: styles.chartTooltip },
      React.createElement(
        'dl',
        null,
        ...[
          ttsegment &&
            React.createElement(
              React.Fragment,
              { key: 'seg' },
              React.createElement('dt', null, 'Group'),
              React.createElement('dd', null, ttsegment.get('name')),
            ),
          ttgoal &&
            ttcampaign &&
            React.createElement(
              React.Fragment,
              { key: 'cpn' },
              React.createElement('dt', null, 'Campaign'),
              React.createElement('dd', null, ttcampaign.get('name')),
            ),
          ttgoal &&
            ttflight &&
            React.createElement(
              React.Fragment,
              { key: 'flt' },
              React.createElement('dt', null, 'Flight'),
              React.createElement('dd', null, ttflight.get('name')),
            ),
          category &&
            React.createElement(
              React.Fragment,
              { key: 'cat' },
              React.createElement('dt', null, 'Category'),
              React.createElement('dd', null, category),
            ),
          ttroi &&
            React.createElement(
              React.Fragment,
              { key: 'roi' },
              React.createElement('dt', null, 'Conversion value'),
              React.createElement('dd', null, numeral(ttroi).format('$0,0.00')),
            ),
          ttnum &&
            React.createElement(
              React.Fragment,
              { key: 'num' },
              React.createElement('dt', null, 'Conversions'),
              React.createElement('dd', null, numeral(ttnum).format('0,0')),
            ),
        ].filter(Boolean),
      ),
    ) as JSX.Element;
  };
}

function sortFn(a: IChartData, b: IChartData): number {
  const aId = ObjectId.createFromHexString(a.segmentId);
  const bId = ObjectId.createFromHexString(b.segmentId);
  return aId.generationTime - bId.generationTime;
}

export function buildBreakdownData(
  aggregatedStats: AggregatedStat,
  attributionModel: TAttributionModel,
): IBreakdownData {
  const roi = aggregatedStats.get('conversions')?.[attributionModel]?.roi || 0;
  const numConversions = aggregatedStats.get('conversions')?.[attributionModel]?.num || 0;

  const goalPieData: Record<string, IChartData> = {};
  const categoryPieData: Record<string, IChartData> = {};
  const segmentPieData: Record<string, IChartData> = {};

  const segmentsStats =
    aggregatedStats.get('conversions')?.[attributionModel]?.segments_breakdown || [];
  segmentsStats.forEach((segmentStats) => {
    if (!segmentStats.goal_segment_id) {
      return;
    }
    if (segmentPieData[segmentStats.goal_segment_id]) {
      segmentPieData[segmentStats.goal_segment_id].num += segmentStats.num || 0;
      segmentPieData[segmentStats.goal_segment_id].roi += segmentStats.roi || 0;
    } else {
      segmentPieData[segmentStats.goal_segment_id] = {
        id: segmentStats.goal_segment_id,
        num: segmentStats.num || 0,
        roi: segmentStats.roi || 0,
        segmentId: segmentStats.goal_segment_id,
      };
    }
    const goalsStats = segmentStats.goals || [];
    goalsStats.forEach((goalStat) => {
      if (!goalStat.goal_id) {
        return;
      }
      if (goalPieData[goalStat.goal_id]) {
        goalPieData[goalStat.goal_id].num += goalStat.num || 0;
        goalPieData[goalStat.goal_id].roi += goalStat.roi || 0;
      } else {
        goalPieData[goalStat.goal_id] = {
          id: goalStat.goal_id,
          num: goalStat.num || 0,
          roi: goalStat.roi || 0,
          goalId: goalStat.goal_id,
          segmentId: segmentStats.goal_segment_id!,
        };
      }
      const categoriesStats = goalStat.categories || [];
      categoriesStats.forEach((catStats): void => {
        if (!catStats.category) {
          return;
        }
        const formattedCat = capitalize(catStats.category);
        const key = `${formattedCat}.${goalStat.goal_id}.${segmentStats.goal_segment_id}`;
        if (categoryPieData[key]) {
          categoryPieData[key].num += catStats.num || 0;
          categoryPieData[key].roi += catStats.roi || 0;
        } else {
          categoryPieData[key] = {
            id: formattedCat,
            name: formattedCat,
            num: catStats.num || 0,
            roi: catStats.roi || 0,
            segmentId: segmentStats.goal_segment_id!,
            goalId: goalStat.goal_id,
            category: formattedCat,
          };
        }
      });
    });
  });

  const segmentPieDataFormatted = Object.values(segmentPieData)
    .filter((d) => d.num > 0)
    .sort(sortFn);
  const goalPieDataFormatted = Object.values(goalPieData)
    .filter((d) => d.num > 0)
    .sort(sortFn);
  const categoryPieDataFormatted = Object.values(categoryPieData)
    .filter((d) => d.num > 0)
    .sort(sortFn);

  const showSegsChart = segmentPieDataFormatted.length > 1;
  const showGoalsChart = goalPieDataFormatted.length > 1;
  const showCatsChart =
    categoryPieDataFormatted.reduce((arr, d) => {
      if (d.category && !arr.includes(d.category)) {
        arr.push(d.category);
      }
      return arr;
    }, [] as string[]).length > 1;
  const showConversionBreakdown = numConversions > 0 && (showSegsChart || showCatsChart);
  const showROIBreakdown = roi > 0 && (showGoalsChart || showCatsChart);
  const breakdownSize: 'sm' | 'lg' = showConversionBreakdown && showROIBreakdown ? 'sm' : 'lg';
  const showBreakdowns = showConversionBreakdown || showROIBreakdown;

  const segmentColors = segmentPieDataFormatted.map((item, index) => ({
    segment: item.segmentId,
    color: CELL_COLORS[index % CELL_COLORS.length],
  }));
  const goalColors = goalPieDataFormatted.map((item, index) => {
    const parentColor = segmentColors.find((seg) => seg.segment === item.segmentId);
    return {
      segment: item.segmentId,
      color: shade(parentColor ? parentColor.color : CELL_COLORS[index % CELL_COLORS.length], -0.1),
    };
  });
  const catColors = categoryPieDataFormatted.map((item, index) => {
    const parentColor = segmentColors.find((seg) => seg.segment === item.segmentId);
    return {
      segment: item.segmentId,
      color: shade(parentColor ? parentColor.color : CELL_COLORS[index % CELL_COLORS.length], -0.2),
    };
  });

  const conversionsLabelContent = showROIBreakdown ? LeftLabelContent : CenterLabelContent;
  const roiLabelContent = showConversionBreakdown ? RightLabelContent : CenterLabelContent;

  return {
    segmentPieDataFormatted,
    goalPieDataFormatted,
    categoryPieDataFormatted,
    showSegsChart,
    showGoalsChart,
    showCatsChart,
    showConversionBreakdown,
    showROIBreakdown,
    showBreakdowns,
    breakdownSize,
    segmentColors,
    goalColors,
    catColors,
    conversionsLabelContent,
    roiLabelContent,
  };
}
